import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { SaveButton, useNotify } from 'react-admin';
import { useForm } from 'react-final-form';
import { describeInvalidForm, describeServerValidation, flattenErrorSources } from './formErrors';

// How long a validation message stays up. The stock 4s is too short to read a
// list of field names, and this is the only feedback the user gets.
const NOTIFICATION_MS = 10000;

/**
 * A SaveButton that says WHICH fields are wrong (NBU-9).
 *
 * Replaces two unhelpful messages:
 *
 *  - client-side, react-admin's stock "The form is not valid. Please check for
 *    errors", which names no field and no tab;
 *  - server-side, the insights API's summary count ("1 field failed
 *    validation"), which is all react-admin's default onFailure shows even
 *    though the response carries a per-field `details` array
 *    (insights routes/api/v1/errors.js). On the campaign forms those details
 *    are the booking-ceiling, missing-spend-limit and IP-list-ownership rules
 *    in routes/console/advertiser_guard.js.
 *
 * Pair it with ErrorAwareFormTabs, which marks and navigates to the offending
 * tab; this button supplies the names, those tabs supply the location.
 */
export const ValidatingSaveButton = (props) => {
  const { onClick, onFailure, ...rest } = props;
  const notify = useNotify();
  const form = useForm();

  const handleClick = useCallback((event) => {
    const sources = flattenErrorSources(form.getState().errors);
    const message = describeInvalidForm(sources);
    if (message) {
      // messageArgs._ is react-admin's documented escape hatch for a message
      // that is literal text rather than a translation key - its own default
      // onFailure uses the same mechanism. Without it the text would be looked
      // up as a key first.
      notify(message, { type: 'warning', messageArgs: { _: message }, autoHideDuration: NOTIFICATION_MS });
    }
    if (typeof onClick === 'function') onClick(event);
  }, [form, notify, onClick]);

  const handleFailure = useCallback((error) => {
    if (typeof onFailure === 'function') return onFailure(error);
    const detailed = describeServerValidation(error);
    const message = detailed ||
      (error && error.message) ||
      'ra.notification.http_error';
    notify(message, { type: 'warning', messageArgs: { _: message }, autoHideDuration: NOTIFICATION_MS });
  }, [notify, onFailure]);

  return (
    <SaveButton
      {...rest}
      // Suppressing react-admin's own invalid-form notification, NOT the
      // validation itself. SaveButton notifies the generic
      // "ra.message.invalid_form" whenever `invalid` is true, and react-admin's
      // notification reducer QUEUES rather than replaces - so leaving it on
      // would show the useless message first and ours only after it timed out.
      // Everything else `invalid` would have changed is unaffected: the button
      // is not disabled by it, and handleSubmitWithRedirect still runs, so
      // final-form still refuses the submit and still marks every field touched
      // (which is what raises the tab badges).
      invalid={false}
      onClick={handleClick}
      onFailure={handleFailure} />
  );
};

ValidatingSaveButton.propTypes = {
  onClick: PropTypes.func,
  onFailure: PropTypes.func
};

export default ValidatingSaveButton;
